import { useId, useState, type ReactNode } from "react";
import { amountFrom, D, type Amount, type YesNo } from "@ftr/tax-core";
import { usd } from "../lib/format.ts";

function textOf(a: Amount): string {
  if (a.state === "value") {
    if (a.raw) return a.raw;
    const [int, frac] = new D(a.value).abs().toFixed().split(".");
    const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (frac ? "." + frac : "");
    return new D(a.value).isNeg() ? "-" + grouped : grouped;
  }
  if (a.state === "unreadable") return a.raw;
  return "";
}

/**
 * A money box that keeps "left empty" distinct from zero. `emptyMeans` says what an empty box records:
 * "blank" for a box copied from a form that is empty there, "unknown" for something not answered yet.
 */
export function AmountInput(props: {
  label: ReactNode;
  value: Amount;
  onChange: (a: Amount) => void;
  emptyMeans: "blank" | "unknown";
  allowNegative?: boolean;
  hint?: ReactNode;
  none?: { label: string };
}) {
  const id = useId();
  const [text, setText] = useState(() => textOf(props.value));
  const isNone = props.value.state === "not_applicable";
  const negative = props.value.state === "value" && new D(props.value.value).isNeg() && !props.allowNegative;
  const error = props.value.state === "unreadable" ? "Enter an amount like 12,500 or (1,500.00)" : negative ? "This can't be negative" : null;

  const set = (raw: string) => {
    setText(raw);
    const a = amountFrom(raw);
    props.onChange(a.state === "blank" ? (props.emptyMeans === "blank" ? a : { state: "unknown" }) : a);
  };

  return (
    <div className={"field" + (error ? " field--error" : "")}>
      <label htmlFor={id}>{props.label}</label>
      {!isNone && (
        <div className="money-input">
          <span aria-hidden="true">$</span>
          <input id={id} inputMode="decimal" autoComplete="off" value={text} onChange={(e) => set(e.target.value)} aria-invalid={Boolean(error)} />
        </div>
      )}
      {props.none && (
        <label className="check">
          <input
            type="checkbox"
            checked={isNone}
            onChange={(e) => {
              if (e.target.checked) props.onChange({ state: "not_applicable" });
              else set(text);
            }}
          />
          {props.none.label}
        </label>
      )}
      {error ? <p className="field-msg">{error}</p> : props.hint ? <p className="field-hint">{props.hint}</p> : null}
    </div>
  );
}

export function amountOk(a: Amount, allowNegative = false): boolean {
  if (a.state === "unreadable") return false;
  if (a.state === "value" && !allowNegative && new D(a.value).isNeg()) return false;
  return true;
}

export function TextInput(props: { label: ReactNode; value: string; onChange: (v: string) => void; hint?: ReactNode; placeholder?: string; list?: string }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{props.label}</label>
      <input id={id} className="text-input" value={props.value} placeholder={props.placeholder} list={props.list} onChange={(e) => props.onChange(e.target.value)} />
      {props.hint && <p className="field-hint">{props.hint}</p>}
    </div>
  );
}

export function DateInput(props: { label: ReactNode; value: string | null; onChange: (v: string | null) => void; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{props.label}</label>
      <input id={id} type="date" className="text-input" value={props.value ?? ""} onChange={(e) => props.onChange(e.target.value || null)} />
      {props.hint && <p className="field-hint">{props.hint}</p>}
    </div>
  );
}

export function Chips<T extends string>(props: { legend: ReactNode; value: T | null; options: { value: T; label: string }[]; onChange: (v: T) => void; hint?: ReactNode }) {
  const name = useId();
  return (
    <fieldset className="choice">
      <legend>{props.legend}</legend>
      {props.hint && <p className="field-hint">{props.hint}</p>}
      <div className="choice-row">
        {props.options.map((o) => (
          <label key={o.value} className={"chip" + (props.value === o.value ? " chip--on" : "")}>
            <input type="radio" name={name} value={o.value} checked={props.value === o.value} onChange={() => props.onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function YesNoField(props: { legend: ReactNode; value: YesNo; onChange: (v: "yes" | "no") => void; hint?: ReactNode }) {
  return (
    <Chips
      legend={props.legend}
      hint={props.hint}
      value={props.value}
      options={[
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ]}
      onChange={props.onChange}
    />
  );
}

/** Shows an Amount for reading: a dollar figure, or what state it's in. */
export function AmountText({ a }: { a: Amount }) {
  switch (a.state) {
    case "value":
      return <span className="figure">{usd(new D(a.value), { cents: true })}</span>;
    case "blank":
      return <span className="muted">blank</span>;
    case "not_applicable":
      return <span className="muted">none</span>;
    case "unreadable":
      return <span className="warn-text">unreadable</span>;
    default:
      return <span className="muted">not entered</span>;
  }
}

export function Money({ v, sign }: { v: string; sign?: boolean }) {
  return <span className="figure">{usd(new D(v), { cents: true, sign })}</span>;
}
